/**
 * Reports tab — Phase 2C-ii.2.
 *
 * Operational-QA artefacts. Type selector + scope selector + generate +
 * job status with polling, download, re-run, soft delete. Tier-gates Report 1
 * (Starter+) and Report 2 (Pro). Demo / Starter see ghost states for
 * unavailable types.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { Download, Loader2, RotateCcw, Trash2, Sparkles } from "lucide-react";
import { toast } from "sonner";

import { useTier } from "@/contexts/AuthContext";
import { authService, formatApiError } from "@/services/authService";

const TIER_RANK = { demo: 0, starter: 1, professional: 2 };
const meets = (cur, req) => (TIER_RANK[cur] || 0) >= (TIER_RANK[req] || 0);

const REPORT_TYPES = [
    { id: "report_1", label: "Maturity, In-Place Strength & STO Yield Report",
      sub: "Operational QA · monitored maturity + in-place strength + yield milestones.",
      min: "starter" },
    { id: "report_2", label: "Thermal Cracking & Thermal Control Plan Report",
      sub: "Pre-pour design + compliance with governing spec.",
      min: "professional" },
];

const statusPillTone = (s) => s === "succeeded" ? "teal" : s === "failed" ? "amber" : "mono";

export default function ReportsTab() {
    const { project } = useOutletContext() || {};
    const { tier } = useTier();
    const projectId = project?.id;

    const [selectedType, setSelectedType] = useState("report_1");
    const [pours, setPours] = useState([]);
    const [pourId, setPourId] = useState("");
    const [jobs, setJobs] = useState([]);
    const [busy, setBusy] = useState(false);
    const [modalOpen, setModalOpen] = useState(false);
    const [optSto, setOptSto] = useState(false);
    const pollRef = useRef(null);

    const refresh = useCallback(async () => {
        if (!projectId) return;
        try {
            const [pp, jj] = await Promise.all([
                authService.http.get(`/projects/${projectId}/pours`),
                authService.http.get(`/reports/jobs?project_id=${projectId}`),
            ]);
            setPours(pp.data || []);
            setJobs(jj.data || []);
        } catch (err) {
            // silent — tab still useable
            console.warn("reports refresh", err);
        }
    }, [projectId]);

    useEffect(() => { refresh(); }, [refresh]);

    // 2 s polling while any job for this project is queued/running
    useEffect(() => {
        const anyActive = jobs.some((j) => j.status === "queued" || j.status === "running");
        if (anyActive && !pollRef.current) {
            pollRef.current = setInterval(refresh, 2000);
        } else if (!anyActive && pollRef.current) {
            clearInterval(pollRef.current); pollRef.current = null;
        }
        return () => { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } };
    }, [jobs, refresh]);

    const monitoredPourIds = useMemo(
        () => new Set(pours.filter((p) => p.has_monitoring).map((p) => p.id)),
        [pours],
    );
    const eligible = pours.filter((p) => monitoredPourIds.has(p.id) || true);  // backend filters; surface all
    const tierMin = REPORT_TYPES.find((t) => t.id === selectedType)?.min || "starter";
    const tierOk = meets(tier, tierMin);
    const scopeValid = selectedType === "report_2" ? true : Boolean(pourId);
    const canGenerate = tierOk && scopeValid && !busy;

    const onGenerateClick = () => {
        // Report 1 has STO/Yield toggle → open the options modal.
        // Report 2 has no options at this checkpoint → dispatch directly.
        if (selectedType === "report_1") {
            setModalOpen(true);
        } else {
            generate();
        }
    };

    const generate = async () => {
        setBusy(true);
        try {
            const body = selectedType === "report_1"
                ? { pour_id: pourId, options: { include_sto_yield: optSto } }
                : { options: {} };
            await authService.http.post(`/projects/${projectId}/reports/${selectedType}/generate`, body);
            toast.success("Report dispatched.");
            setModalOpen(false);
            await refresh();
        } catch (err) {
            toast.error(formatApiError(err) || "Could not dispatch report.");
        } finally {
            setBusy(false);
        }
    };

    const onDownload = async (job) => {
        try {
            const res = await authService.http.get(`/reports/${job.id}/download`, { responseType: "blob" });
            const url = URL.createObjectURL(res.data);
            const a = document.createElement("a");
            a.href = url; a.download = `concrete-control-${job.report_type}-${job.id.slice(0,8)}.pdf`;
            a.click(); URL.revokeObjectURL(url);
        } catch (err) { toast.error(formatApiError(err) || "Download failed."); }
    };

    const onRerun = async (job) => {
        const body = {
            pour_id: job.inputs_snapshot?.pour_id,
            mix_id: job.inputs_snapshot?.mix_id,
            options: job.inputs_snapshot?.options || {},
        };
        try {
            await authService.http.post(`/projects/${projectId}/reports/${job.report_type}/generate`, body);
            toast.success("Re-run dispatched.");
            await refresh();
        } catch (err) { toast.error(formatApiError(err) || "Re-run failed."); }
    };

    const onDelete = async (job) => {
        if (!window.confirm("Soft-delete this report job?")) return;
        try {
            await authService.http.delete(`/reports/jobs/${job.id}`);
            await refresh();
        } catch (err) { toast.error(formatApiError(err) || "Delete failed."); }
    };

    return (
        <div data-testid="reports-tab" className="flex flex-col gap-10">
            <header className="flex flex-col gap-2">
                <span className="tc-eyebrow text-[#01696f]">Reports</span>
                <h2 className="text-[22px] font-medium tracking-tight text-[#0e1416]">
                    Operational QA artefacts &amp; thermal control plans
                </h2>
                <p className="text-[13.5px] text-[#5a6669] leading-[1.65] max-w-[78ch]">
                    Generate engineering PDFs that read from canonical SolverResult,
                    monitored estimate, and yield-record contracts. Provenance is
                    preserved in every PDF.
                </p>
            </header>

            {/* A — Type selector */}
            <section data-testid="reports-section-type" className="flex flex-col gap-3">
                <SectionLabel index="A" title="Report type" />
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-px bg-[#d8dcdd] border border-[#d8dcdd]">
                    {REPORT_TYPES.map((rt) => {
                        const locked = !meets(tier, rt.min);
                        const active = selectedType === rt.id;
                        return (
                            <button
                                key={rt.id}
                                type="button"
                                onClick={() => setSelectedType(rt.id)}
                                data-testid={`report-type-${rt.id}`}
                                className={`text-left p-5 bg-[#ffffff] transition-colors relative border-l-2 ${
                                    active ? "border-[#01696f]" : "border-transparent"
                                } hover:bg-[#f6f7f6]`}
                            >
                                <div className="flex items-baseline justify-between">
                                    <span className="text-[14px] font-medium text-[#0e1416]">{rt.label}</span>
                                    {locked && (
                                        <span className="tc-mono text-[9.5px] tracking-[0.16em] uppercase text-[#5a6669] border border-[#5a6669] px-1.5 py-[2px]">
                                            {rt.min === "professional" ? "Pro" : "Starter+"}
                                        </span>
                                    )}
                                </div>
                                <p className="mt-2 text-[12px] text-[#5a6669] leading-[1.55]">{rt.sub}</p>
                                {locked && (
                                    <p className="mt-3 tc-mono text-[11px] text-[#8a9498]">
                                        Upgrade to {rt.min === "professional" ? "Professional" : "Starter or higher"} to generate this report.
                                    </p>
                                )}
                            </button>
                        );
                    })}
                </div>
            </section>

            {/* B — Scope (Report 1 only) */}
            {selectedType === "report_1" && (
                <section data-testid="reports-section-scope" className="flex flex-col gap-3">
                    <SectionLabel index="B" title="Scope" />
                    {pours.length === 0 ? (
                        <p className="tc-mono text-[11.5px] text-[#5a6669] border border-dashed border-[#d8dcdd] bg-[#f6f7f6] px-4 py-3">
                            No pours found. Add a pour in Setup to enable Report 1.
                        </p>
                    ) : (
                        <div className="flex flex-col gap-2 max-w-[480px]">
                            <Label>Pour</Label>
                            <select
                                value={pourId}
                                onChange={(e) => setPourId(e.target.value)}
                                data-testid="reports-pour-select"
                                className="tc-mono text-[13px] px-3 py-2 border border-[#d8dcdd] bg-[#ffffff] text-[#0e1416] focus:outline-none focus:border-[#01696f]"
                            >
                                <option value="">— select a pour with monitoring data —</option>
                                {pours.map((p) => (
                                    <option key={p.id} value={p.id}>
                                        #{p.sequence_index} {p.name || "Pour"} · {p.status}
                                    </option>
                                ))}
                            </select>
                            <p className="tc-mono text-[10.5px] text-[#8a9498]">
                                Report 1 reads from the pour&apos;s monitored StrengthEstimate and yield records.
                            </p>
                        </div>
                    )}
                </section>
            )}

            {/* B — Scope (Report 2: project-level, no pour dropdown) */}
            {selectedType === "report_2" && (
                <section data-testid="reports-section-scope-r2" className="flex flex-col gap-3">
                    <SectionLabel index="B" title="Scope" />
                    <div className="flex flex-col gap-2 max-w-[640px]">
                        <Label>Project</Label>
                        <div data-testid="reports-r2-scope-project"
                             className="tc-mono text-[13px] px-3 py-2 border border-[#d8dcdd] bg-[#f6f7f6] text-[#0e1416]">
                            {project?.name || "—"}
                        </div>
                        <p className="tc-mono text-[10.5px] text-[#8a9498] leading-[1.55]">
                            Report 2 covers the proposed mass placement at the project level:
                            mix, geometry, environmental scenario, predicted SolverResult and the
                            governing spec preset. Field-verification data lives in Report 1.
                        </p>
                    </div>
                </section>
            )}

            {/* C — Generate */}
            <section data-testid="reports-section-generate" className="flex items-center justify-between gap-4 border-t border-[#d8dcdd] pt-5">
                <div className="flex flex-col">
                    <span className="tc-mono text-[10.5px] tracking-widest uppercase text-[#5a6669]">
                        Selected
                    </span>
                    <span className="text-[13px] text-[#0e1416] mt-1">
                        {REPORT_TYPES.find((t) => t.id === selectedType)?.label}
                    </span>
                </div>
                <button
                    onClick={onGenerateClick}
                    disabled={!canGenerate}
                    data-testid="reports-generate-button"
                    title={!tierOk ? "Upgrade required" : !scopeValid ? "Select a pour" : ""}
                    className="tc-mono text-[12px] px-5 py-2.5 bg-[#01696f] hover:bg-[#015a60] disabled:opacity-40 disabled:cursor-not-allowed text-[#ffffff] inline-flex items-center gap-2 transition-colors"
                >
                    {busy ? <Loader2 size={13} className="animate-spin" /> : <Sparkles size={13} strokeWidth={1.5} />}
                    Generate report
                </button>
            </section>

            {/* D — Job status panel */}
            <section data-testid="reports-section-jobs" className="flex flex-col gap-3">
                <SectionLabel index="D" title="Recent jobs" />
                <div className="border border-[#d8dcdd] bg-[#ffffff]">
                    {jobs.length === 0 ? (
                        <div className="px-5 py-7 text-center text-[13px] text-[#5a6669]">
                            No reports yet. Generate one above.
                        </div>
                    ) : jobs.map((j) => (
                        <div
                            key={j.id}
                            data-testid={`report-job-${j.id}`}
                            className="grid grid-cols-1 md:grid-cols-12 gap-3 px-5 py-3.5 border-b border-[#d8dcdd] last:border-b-0 items-baseline"
                        >
                            <div className="md:col-span-4 flex flex-col gap-1">
                                <span className="tc-mono text-[11px] text-[#0e1416] inline-flex items-center gap-2">
                                    {j.report_type}
                                    {j.inputs_snapshot?.options?.include_sto_yield && (
                                        <span data-testid={`report-job-${j.id}-sto-pill`}
                                              className="tc-mono text-[9px] tracking-[0.16em] uppercase text-[#01696f] border border-[#01696f] px-1 py-[1px] leading-none">
                                            STO+
                                        </span>
                                    )}
                                </span>
                                <span className="tc-mono text-[10px] text-[#8a9498]">
                                    {j.inputs_snapshot?.pour_id ? `pour ${j.inputs_snapshot.pour_id.slice(0,8)}…` : "project-wide"}
                                </span>
                            </div>
                            <div className="md:col-span-2">
                                <StatusPill status={j.status} />
                            </div>
                            <div className="md:col-span-3 tc-mono text-[10.5px] text-[#5a6669]">
                                {j.finished_at ? new Date(j.finished_at).toLocaleString()
                                    : j.started_at ? `running · ${new Date(j.started_at).toLocaleTimeString()}`
                                    : new Date(j.created_at).toLocaleString()}
                            </div>
                            <div className="md:col-span-3 flex gap-2 justify-end">
                                {j.status === "succeeded" && (
                                    <button onClick={() => onDownload(j)}
                                            data-testid={`report-download-${j.id}`}
                                            className="tc-mono text-[11px] px-2.5 py-1.5 border border-[#01696f] text-[#01696f] hover:bg-[rgba(1,105,111,0.06)] inline-flex items-center gap-1">
                                        <Download size={12} strokeWidth={1.5} /> PDF
                                    </button>
                                )}
                                {j.status === "succeeded" && (
                                    <button onClick={() => onRerun(j)}
                                            data-testid={`report-rerun-${j.id}`}
                                            className="tc-mono text-[11px] px-2.5 py-1.5 border border-[#d8dcdd] text-[#5a6669] hover:border-[#01696f] hover:text-[#01696f] inline-flex items-center gap-1">
                                        <RotateCcw size={12} strokeWidth={1.5} /> Re-run
                                    </button>
                                )}
                                <button onClick={() => onDelete(j)}
                                        data-testid={`report-delete-${j.id}`}
                                        className="tc-mono text-[11px] px-2.5 py-1.5 border border-[#d8dcdd] text-[#a13434] hover:border-[#a13434]">
                                    <Trash2 size={12} strokeWidth={1.5} />
                                </button>
                            </div>
                            {j.status === "failed" && j.error && (
                                <div className="md:col-span-12 mt-2 tc-mono text-[10.5px] text-[#a13434]">
                                    {j.error}
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            </section>

            <p data-testid="reports-footer-caption"
               className="tc-mono text-[10.5px] tracking-wider text-[#5a6669] border-t border-[#d8dcdd] pt-5 leading-[1.6]">
                Reports v0.1 · operational QA artefacts derived from monitored
                maturity and Yield outputs. Solver provenance preserved in each
                PDF.
            </p>

            {modalOpen && (
                <div data-testid="reports-options-modal"
                     className="fixed inset-0 z-50 bg-[rgba(14,20,22,0.55)] flex items-center justify-center p-6"
                     onClick={() => setModalOpen(false)}>
                    <div className="bg-[#ffffff] border border-[#d8dcdd] shadow-xl max-w-[520px] w-full p-7"
                         onClick={(e) => e.stopPropagation()}>
                        <span className="tc-eyebrow text-[#01696f]">Generate report</span>
                        <h3 className="mt-2 text-[18px] font-medium tracking-tight text-[#0e1416]">
                            Generate Maturity, In-Place Strength
                            {optSto ? " & STO Yield" : ""} Report
                        </h3>
                        {selectedType === "report_1" && (
                            <>
                                <label className="mt-5 flex items-start gap-3 cursor-pointer">
                                    <input type="checkbox" checked={optSto}
                                           data-testid="reports-modal-sto-toggle"
                                           onChange={(e) => setOptSto(e.target.checked)}
                                           className="mt-1 accent-[#01696f]" />
                                    <span className="flex flex-col gap-1">
                                        <span className="text-[13px] text-[#0e1416]">Include STO / Yield section</span>
                                        <span className="tc-mono text-[10.5px] text-[#8a9498] leading-[1.55]">
                                            Adds the schedule-compression and yield breakdown from monitored milestones.
                                            Default export is the lean technical QA record.
                                        </span>
                                    </span>
                                </label>
                            </>
                        )}
                        <div className="mt-6 flex flex-wrap gap-3 justify-end">
                            <button onClick={() => setModalOpen(false)} data-testid="reports-modal-cancel"
                                    className="tc-mono text-[12px] px-4 py-2 border border-[#d8dcdd] hover:border-[#0e1416] text-[#0e1416]">
                                Cancel
                            </button>
                            <button onClick={generate} disabled={busy} data-testid="reports-modal-generate"
                                    className="tc-mono text-[12px] px-4 py-2 bg-[#01696f] hover:bg-[#015a60] disabled:opacity-50 text-[#ffffff] inline-flex items-center gap-2">
                                {busy && <Loader2 size={12} className="animate-spin" />}
                                Generate
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

const SectionLabel = ({ index, title }) => (
    <div className="flex items-baseline gap-5">
        <span className="tc-mono text-[10.5px] tracking-widest uppercase text-[#01696f]">{index}</span>
        <h3 className="text-[16.5px] font-medium tracking-tight text-[#0e1416]">{title}</h3>
        <div className="flex-1 h-px bg-[#d8dcdd]" />
    </div>
);

const Label = ({ children }) => (
    <span className="tc-mono text-[10.5px] tracking-widest uppercase text-[#5a6669]">{children}</span>
);

const StatusPill = ({ status }) => {
    const tone = statusPillTone(status);
    const cls = {
        teal:  "border-[#01696f] text-[#01696f] bg-[rgba(1,105,111,0.06)]",
        mono:  "border-[#5a6669] text-[#5a6669]",
        amber: "border-[#b3741f] text-[#b3741f] bg-[rgba(179,116,31,0.06)]",
    }[tone];
    return (
        <span className={`inline-flex items-center tc-mono text-[9.5px] tracking-[0.16em] uppercase px-1.5 py-[2px] border ${cls} leading-none`}>
            {status}
        </span>
    );
};
